/**
 * Global exchange rates (`exchange_rate` table, read-only, USD pivot).
 *
 * Server refreshes table every 12h from ECB (Frankfurter) and PowerSync
 * syncs it to all clients. Each row is `id = ISO 4217 code`,
 * `rate = units of that currency per 1 USD` (string: PG numeric ≡ TEXT in
 * client SQLite) and `as_of = date of data`.
 *
 * Helpers in this module are PURE: UI decides what to do when a rate is
 * missing (helpers return `null`; recommended fallback is 1:1 with
 * visible warning).
 */
import {
  and,
  asc,
  eq,
  exchange_rate,
  type PowerSyncDrizzleDB,
  user_exchange_rate,
} from "@cashsync/powersync";
import { liveUserExchangeRateFilters } from "../live/filters";
import { convertMinorUnits } from "../utils/currency";

export interface ExchangeRateRow {
  /** ISO 4217 code (e.g. "EUR"). */
  id: string;
  /** Rate vs USD as decimal string (PG numeric ≡ text on client). */
  rate: string | null;
  as_of: string | null;
}

/** ISO code → rate vs USD map (decimal string), canonical consumer shape. */
export type ExchangeRatesMap = ReadonlyMap<string, string>;

/** Complete catalog of synchronized rates. */
export function getExchangeRatesQuery(db: PowerSyncDrizzleDB) {
  return db.select().from(exchange_rate);
}

/** Builds rates map from synchronized rows. */
export function ratesMapFromRows(
  rows: readonly { id: string; rate: string | null }[] | undefined,
): ExchangeRatesMap {
  const map = new Map<string, string>();
  for (const row of rows ?? []) {
    if (row.rate !== null) {
      map.set(row.id.toUpperCase(), row.rate);
    }
  }
  return map;
}

/** Rate of a currency vs USD; USD ≡ 1 implicit. `null` if missing or invalid. */
function usdRate(currency: string, rates: ExchangeRatesMap): number | null {
  if (currency === "USD") {
    return 1;
  }
  const value = Number(rates.get(currency));
  return Number.isFinite(value) && value > 0 ? value : null;
}

/**
 * Conversion rate `from → to` via USD pivot (multiplying a decimal
 * amount in `from` by the rate yields the decimal in `to`).
 * Returns `null` if any rate is missing.
 */
export function getRate(rates: ExchangeRatesMap, from: string, to: string): number | null {
  if (from === to) {
    return 1;
  }
  const fromRate = usdRate(from, rates);
  const toRate = usdRate(to, rates);
  if (fromRate === null || toRate === null) {
    return null;
  }
  return toRate / fromRate;
}

/**
 * Converts an amount in minor units between currencies using USD as
 * pivot. Returns null if any rate is missing.
 *
 * NOTE: operates directly on minor units (assumes same decimals in
 * both currencies). To materialize amounts use `materializeBaseAmount`, which
 * respects ISO decimals of each currency via `convertMinorUnits`.
 */
export function convertAmount(
  amount: number,
  fromCurrency: string,
  toCurrency: string,
  rates: ExchangeRatesMap,
): number | null {
  const rate = getRate(rates, fromCurrency, toCurrency);
  return rate === null ? null : Math.round(amount * rate);
}

export interface MaterializedBaseAmount {
  /** Amount in minor units of base currency. */
  baseAmount: number;
  /** Applied rate (from → base); `null` when no conversion occurred (same currency). */
  rateUsed: number | null;
}

/**
 * Materializes `base_amount` for a transaction (decision D2: frozen when
 * recording, never recalculated).
 *
 * - Same currency → identity (`rateUsed: null`).
 * - `overrideRate` (user manual rate, decimal `from → base`) takes
 *   precedence over synced rates.
 * - Missing/invalid rate → `null`; UI decides fallback (1:1 + warning).
 */
export function materializeBaseAmount(
  amountMinor: number,
  currency: string,
  baseCurrency: string,
  rates: ExchangeRatesMap,
  overrideRate?: number | null,
): MaterializedBaseAmount | null {
  if (currency === baseCurrency) {
    return { baseAmount: amountMinor, rateUsed: null };
  }
  const rate = overrideRate ?? getRate(rates, currency, baseCurrency);
  if (rate === null || !Number.isFinite(rate) || rate <= 0) {
    return null;
  }
  return {
    baseAmount: convertMinorUnits(amountMinor, currency, baseCurrency, rate),
    rateUsed: rate,
  };
}

/** Most recent `as_of` date in catalog, or `null` if empty. */
export function latestRateDate(
  rows: readonly { as_of: string | null }[] | undefined,
): string | null {
  let latest: string | null = null;
  for (const row of rows ?? []) {
    if (row.as_of && (latest === null || row.as_of > latest)) {
      latest = row.as_of;
    }
  }
  return latest;
}

// ─── User rates by date (editable, offline) ──────────────────────────────────
//
// Unlike global catalog (USD pivot, read-only), user can manually set
// `from → base` rate effective FROM a date. Conversion of a transaction
// uses rate whose `as_of` is the most recent NOT exceeding transaction
// date (rate "effective on that date"), not current one.

/** User exchange rate row as received from client SQLite. */
export interface UserExchangeRateRow {
  id: string;
  currency: string | null;
  base_currency: string | null;
  /** Tasa `1 currency = rate base_currency` (string decimal). */
  rate: string | null;
  /** Date (YYYY-MM-DD) from which rate takes effect. */
  as_of: string | null;
}

/**
 * Map `"FROM>BASE"` → list of `{ as_of, rate }` points ordered DESC by
 * date (most recent first). Canonical form for `rateForDate`.
 */
export type UserRatesByDate = ReadonlyMap<string, readonly { asOf: string; rate: number }[]>;

function pairKey(from: string, base: string): string {
  return `${from.toUpperCase()}>${base.toUpperCase()}`;
}

/** User exchange rate catalog (non-deleted). */
export function getUserExchangeRatesQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      id: user_exchange_rate.id,
      currency: user_exchange_rate.currency,
      base_currency: user_exchange_rate.base_currency,
      rate: user_exchange_rate.rate,
      as_of: user_exchange_rate.as_of,
    })
    .from(user_exchange_rate)
    .where(and(eq(user_exchange_rate.user_id, userId), ...liveUserExchangeRateFilters()))
    .orderBy(asc(user_exchange_rate.currency), asc(user_exchange_rate.as_of));
}

/** Builds `from>base → points by date (DESC)` index from rows. */
export function userRatesByDateFromRows(
  rows: readonly UserExchangeRateRow[] | undefined,
): UserRatesByDate {
  const map = new Map<string, { asOf: string; rate: number }[]>();
  for (const row of rows ?? []) {
    if (!row.currency || !row.base_currency || !row.as_of) {
      continue;
    }
    const rate = Number(row.rate);
    if (!Number.isFinite(rate) || rate <= 0) {
      continue;
    }
    const key = pairKey(row.currency, row.base_currency);
    const list = map.get(key) ?? [];
    list.push({ asOf: row.as_of, rate });
    map.set(key, list);
  }
  // Most recent first: simplifies lookup of "effective at date".
  for (const list of map.values()) {
    list.sort((a, b) => (a.asOf < b.asOf ? 1 : a.asOf > b.asOf ? -1 : 0));
  }
  return map;
}

/**
 * User-defined `from → base` rate EFFECTIVE at `dateISO` (the one with
 * most recent `as_of` not exceeding the date). If date precedes the first
 * defined rate, falls back to oldest available. `null` if none exist or
 * currencies match (caller handles identity separately).
 */
export function userRateForDate(
  ratesByDate: UserRatesByDate,
  from: string,
  base: string,
  dateISO: string,
): number | null {
  if (from.toUpperCase() === base.toUpperCase()) {
    return 1;
  }
  const points = ratesByDate.get(pairKey(from, base));
  if (!points || points.length === 0) {
    return null;
  }
  // Accounting day, trimmed to YYYY-MM-DD to compare with `as_of`.
  const day = dateISO.slice(0, 10);
  for (const point of points) {
    if (point.asOf <= day) {
      return point.rate;
    }
  }
  // Date precedes all known rates → use oldest (last element).
  return points.at(-1)?.rate ?? null;
}
